import { css } from "@emotion/css"
import { MdAccountCircle } from "react-icons/md"

import Image from "@p-ban/common/src/components/html/Image"

import useLoginStatusQuery from "../../../../../api/login/useLoginStatusQuery"
import LAYOUT from "../../constant/layout"
import Z_INDEX from "../../constant/zIndex"
import LINKS from "../../constant/links"
import logo from "./1ClickHeaderLogo.png"

type Props = {
  minWidth: number
  maxWidth: number
  onLoginClick?: () => void
}

const COLORS = {
  GRAY: "#636b74",
  GREEN: "#2faa3b",
  HOVER_BG: "#f8f8f8",
} as const

const Header = ({ minWidth, maxWidth, onLoginClick }: Props) => {
  const { isLoggedIn, isLoading } = useLoginStatusQuery()

  const handleLoginClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onLoginClick) {
      e.preventDefault()
      onLoginClick()
    }
  }

  return (
    <header className={styles.header(minWidth)}>
      <div className={styles.container(maxWidth)}>
        <Image src={logo} alt="P板.com" width={140} height={24} />
        {!isLoading && (
          <div className={styles.actions}>
            {isLoggedIn ? (
              <>
                <AccountLink href={LINKS.LOGOUT} label="ログアウト" />
                <PrimaryButton href={LINKS.MYPAGE} label="マイページ" />
              </>
            ) : (
              <>
                <AccountLink href={onLoginClick ? "#" : LINKS.LOGIN} label="ログイン" onClick={handleLoginClick} />
                <PrimaryButton href={LINKS.SIGNUP} label="新規登録" />
              </>
            )}
          </div>
        )}
      </div>
    </header>
  )
}

const AccountLink = ({
  href,
  label,
  onClick,
}: {
  href: string
  label: string
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
}) => (
  <a href={href} className={styles.accountLink} onClick={onClick}>
    <MdAccountCircle size={24} color={COLORS.GRAY} />
    <span className={styles.accountText}>{label}</span>
  </a>
)

const PrimaryButton = ({ href, label }: { href: string; label: string }) => (
  <a href={href} className={styles.primaryButton}>
    {label}
  </a>
)

const styles = {
  header: (minWidth: number) => css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: ${LAYOUT.HEADER_HEIGHT}px;
    z-index: ${Z_INDEX.HEADER};
    min-width: ${minWidth}px;
    background-color: #fff;
    box-shadow: 0 1px 2px 0 rgba(21, 21, 21, 0.08);
  `,
  container: (maxWidth: number) => css`
    max-width: ${maxWidth}px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 32px;
  `,
  actions: css`
    display: flex;
    align-items: center;
    gap: 24px;
  `,
  accountLink: css`
    display: flex;
    align-items: center;
    gap: 4px;
    text-decoration: none;
    cursor: pointer;
    &:hover {
      opacity: 0.7;
    }
  `,
  accountText: css`
    font-family: "Roboto", "Noto Sans JP", sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.17px;
    color: ${COLORS.GRAY};
    white-space: nowrap;
  `,
  primaryButton: css`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #fff;
    border: 1px solid ${COLORS.GREEN};
    border-radius: 20px;
    padding: 8px 16px;
    height: 32px;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
    cursor: pointer;
    text-decoration: none;
    font-family: "Noto Sans JP", sans-serif;
    font-size: 14px;
    font-weight: 700;
    line-height: 24px;
    color: ${COLORS.GREEN};
    white-space: nowrap;
    &:hover {
      background-color: ${COLORS.HOVER_BG};
    }
  `,
}

export default Header
